import { Fragment, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { PageWithTitle } from 'src/Common/components';
import {
  useCosts,
  useCostsMonthly,
  useCreateCost,
  useDeleteCost,
  useRealPnl,
  useDividends,
  useDeleteDividendDistribution,
  usePartners,
  PredictionCost,
  DividendDistribution,
} from '../api/useCosts';
import { useCashflows } from '../api/usePositions';
import { formatUsd, pnlColor } from '../utils/format';
import { DailyPnlTab } from '../components/DailyPnlTab';
import { PartnersPanel } from '../components/PartnersPanel';
import { RecordDistributionDialog } from '../components/RecordDistributionDialog';

const fmtDate = (value: string | undefined | null) => {
  if (!value) return '-';
  return new Date(value).toISOString().split('T')[0];
};

const SummaryCard = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <Paper
    sx={{
      px: 2,
      py: 1.5,
      minWidth: 180,
      backgroundColor: 'background.paper',
    }}
  >
    <Typography
      sx={{
        fontSize: '0.62rem',
        textTransform: 'uppercase',
        color: 'grey.600',
        letterSpacing: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.15rem', fontWeight: 600, color }}>
      {value}
    </Typography>
  </Paper>
);

// Above this many lines the Partners cell stops listing them inline and the
// row gets a chevron to a nested per-line table.
const INLINE_LINES_LIMIT = 2;

type EconomicsTab = 'daily' | 'costs';

export const EconomicsPage = () => {
  const [tab, setTab] = useState<EconomicsTab>('daily');
  const { data: realPnl } = useRealPnl();
  const { data: monthly } = useCostsMonthly();
  const { data: costs } = useCosts();
  const { data: cashflows } = useCashflows();
  const { data: dividends } = useDividends();
  const { data: partners } = usePartners();
  const { mutate: createCost, isLoading: creating } = useCreateCost();
  const { mutate: deleteCost } = useDeleteCost();
  const { mutate: deleteDistribution } = useDeleteDividendDistribution();

  const cashflowSummary = useMemo(() => {
    let deposits = 0;
    let withdrawals = 0;
    for (const cf of cashflows ?? []) {
      if (cf.type === 'deposit') deposits += cf.amountUsd;
      else withdrawals += cf.amountUsd;
    }
    return { deposits, withdrawals, net: deposits - withdrawals };
  }, [cashflows]);

  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [confirmDelete, setConfirmDelete] = useState<PredictionCost | null>(
    null
  );
  const [distributionOpen, setDistributionOpen] = useState(false);
  const [confirmDeleteDistribution, setConfirmDeleteDistribution] =
    useState<DividendDistribution | null>(null);
  const [expandedDistributionId, setExpandedDistributionId] = useState<
    string | null
  >(null);

  const legacyPartnerId = useMemo(
    () => partners?.find(p => p.legacyUnattributed)?._id,
    [partners]
  );

  const canSubmit = useMemo(() => {
    const num = parseFloat(amount);
    return description.trim().length > 0 && !isNaN(num) && num > 0;
  }, [description, amount]);

  const handleAdd = () => {
    if (!canSubmit) return;
    createCost(
      {
        description: description.trim(),
        amountUsd: parseFloat(amount),
        date: new Date(date).toISOString(),
      },
      {
        onSuccess: () => {
          setDescription('');
          setAmount('');
        },
      }
    );
  };

  return (
    <PageWithTitle>
      <Box px={2} py={1} width="100%">
        <Stack direction="row" alignItems="center" mb={1.5}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem', color: 'text.primary' }}>
            Economics
          </Typography>
        </Stack>

        {/* Tabs — mirrors the compact 32px pattern from PositionsPage. */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 32,
              '& .MuiTab-root': {
                minHeight: 32,
                py: 0,
                textTransform: 'none',
                fontSize: '0.82rem',
              },
              '& .MuiTabs-indicator': { height: 2 },
            }}
          >
            <Tab value="daily" label="Daily P&L" />
            <Tab value="costs" label="Costs & Payments" />
          </Tabs>
        </Stack>

        {tab === 'daily' && <DailyPnlTab />}

        {tab === 'costs' && (
          <>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={2}>
          <SummaryCard
            label="P&L"
            value={formatUsd(realPnl?.pnlUsd)}
            color={pnlColor(realPnl?.pnlUsd)}
          />
          <SummaryCard
            label="Total Costs"
            value={formatUsd(realPnl?.totalCostsUsd)}
            color="#f44336"
          />
          <SummaryCard
            label="Real P&L"
            value={formatUsd(realPnl?.realPnlUsd)}
            color={pnlColor(realPnl?.realPnlUsd)}
          />
          <SummaryCard
            label="Dividends Pending"
            value={formatUsd(realPnl?.dividendsPendingUsd)}
            color={pnlColor(realPnl?.dividendsPendingUsd)}
          />
          <SummaryCard
            label="This Month P&L"
            value={formatUsd(dividends?.currentPeriod.periodRealPnlUsd)}
            color={pnlColor(dividends?.currentPeriod.periodRealPnlUsd)}
          />
          <SummaryCard
            label="Dividends Paid"
            value={formatUsd(dividends?.totalDistributedUsd)}
            color="#ff9800"
          />
        </Stack>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography
            sx={{
              fontSize: '0.72rem',
              textTransform: 'uppercase',
              color: 'grey.600',
              mb: 1,
            }}
          >
            Add Cost
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              label="Description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              sx={{ flex: 2, minWidth: 220 }}
            />
            <TextField
              size="small"
              label="Amount (USD)"
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              sx={{ flex: 1, minWidth: 140 }}
            />
            <TextField
              size="small"
              label="Date"
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
            <Button
              variant="contained"
              size="small"
              disabled={!canSubmit || creating}
              onClick={handleAdd}
            >
              Add
            </Button>
          </Stack>
        </Paper>

        <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap">
          <Paper sx={{ p: 1.5, flex: 1, minWidth: 320 }}>
            <Typography
              sx={{
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                color: 'grey.600',
                mb: 1,
              }}
            >
              Month-by-Month
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Costs</TableCell>
                  <TableCell align="right"># Entries</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthly?.monthly.map(row => (
                  <TableRow key={row.month}>
                    <TableCell>{row.month}</TableCell>
                    <TableCell align="right">
                      {formatUsd(row.totalUsd)}
                    </TableCell>
                    <TableCell align="right">{row.count}</TableCell>
                  </TableRow>
                ))}
                {monthly && monthly.monthly.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        No costs yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ p: 1.5, flex: 2, minWidth: 420 }}>
            <Typography
              sx={{
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                color: 'grey.600',
                mb: 1,
              }}
            >
              All Costs
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {costs?.map(cost => (
                  <TableRow key={cost._id}>
                    <TableCell>
                      {new Date(cost.date).toISOString().split('T')[0]}
                    </TableCell>
                    <TableCell>{cost.description}</TableCell>
                    <TableCell align="right">
                      {formatUsd(cost.amountUsd)}
                    </TableCell>
                    <TableCell align="right" sx={{ width: 40 }}>
                      <IconButton
                        size="small"
                        onClick={() => setConfirmDelete(cost)}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {costs && costs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        No costs yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Stack>

        <PartnersPanel />

        <Paper sx={{ p: 1.5, mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            mb={1}
          >
            <Typography
              sx={{
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                color: 'grey.600',
              }}
            >
              Dividend Distributions
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => setDistributionOpen(true)}
            >
              Record Distribution
            </Button>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell align="right">Period Real P&amp;L</TableCell>
                <TableCell align="right">Distributed</TableCell>
                <TableCell>Partners</TableCell>
                <TableCell>Note</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ backgroundColor: 'rgba(33,150,243,0.08)' }}>
                <TableCell>
                  {dividends
                    ? `${fmtDate(dividends.currentPeriod.startDate)} → now`
                    : '-'}
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75,
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      color: '#2196f3',
                    }}
                  >
                    IN PROGRESS
                  </Box>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: pnlColor(dividends?.currentPeriod.periodRealPnlUsd),
                  }}
                >
                  {formatUsd(dividends?.currentPeriod.periodRealPnlUsd)}
                </TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell>—</TableCell>
                <TableCell>—</TableCell>
                <TableCell />
              </TableRow>
              {[...(dividends?.distributions ?? [])].reverse().map(d => {
                const expandable = d.lines.length > INLINE_LINES_LIMIT;
                const expanded = expandedDistributionId === d._id;
                const isLegacy =
                  d.lines.length === 1 &&
                  !!legacyPartnerId &&
                  d.lines[0].partnerId === legacyPartnerId;
                const summary = (
                  expandable ? d.lines.slice(0, INLINE_LINES_LIMIT) : d.lines
                )
                  .map(l => `${l.partnerName} ${formatUsd(l.amountUsd)}`)
                  .join(' · ');
                return (
                  <Fragment key={d._id}>
                    <TableRow>
                      <TableCell>
                        {fmtDate(d.periodStart)} → {fmtDate(d.periodEnd)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: pnlColor(d.periodRealPnlUsd) }}
                      >
                        {formatUsd(d.periodRealPnlUsd)}
                        {d.snapshotIsManual && (
                          <Box
                            component="span"
                            title="P&L entered manually"
                            sx={{
                              ml: 0.5,
                              fontSize: '0.6rem',
                              color: 'grey.500',
                            }}
                          >
                            ✎
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatUsd(d.totalUsd)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>
                        {d.lines.length === 0 ? (
                          '—'
                        ) : (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                            flexWrap="wrap"
                          >
                            <Box component="span">{summary}</Box>
                            {expandable && (
                              <Box
                                component="span"
                                sx={{ color: 'grey.500' }}
                              >
                                +{d.lines.length - INLINE_LINES_LIMIT} more
                              </Box>
                            )}
                            {isLegacy && (
                              <Chip
                                label="histórico"
                                size="small"
                                title="Migrated payment, no partner yet"
                                sx={{ height: 18, fontSize: '0.6rem' }}
                              />
                            )}
                            {expandable && (
                              <IconButton
                                size="small"
                                title={expanded ? 'Collapse' : 'Expand'}
                                onClick={() =>
                                  setExpandedDistributionId(
                                    expanded ? null : d._id
                                  )
                                }
                              >
                                {expanded ? (
                                  <ExpandLessIcon sx={{ fontSize: '1rem' }} />
                                ) : (
                                  <ExpandMoreIcon sx={{ fontSize: '1rem' }} />
                                )}
                              </IconButton>
                            )}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
                        {d.note || '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ width: 40 }}>
                        <IconButton
                          size="small"
                          onClick={() => setConfirmDeleteDistribution(d)}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {expandable && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
                          <Collapse in={expanded} timeout={200} unmountOnExit>
                            <Table size="small" sx={{ mb: 1 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Partner</TableCell>
                                  <TableCell align="right">Amount</TableCell>
                                  <TableCell>Note</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {d.lines.map(line => (
                                  <TableRow key={line._id}>
                                    <TableCell>{line.partnerName}</TableCell>
                                    <TableCell align="right">
                                      {formatUsd(line.amountUsd)}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontSize: '0.75rem',
                                        color: 'grey.500',
                                      }}
                                    >
                                      {line.note || '-'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
              {dividends && dividends.distributions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                      No monthly closes yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Paper sx={{ p: 1.5, mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
            mb={1}
          >
            <Typography
              sx={{
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                color: 'grey.600',
              }}
            >
              Deposits &amp; Withdrawals
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Typography sx={{ fontSize: '0.78rem', color: 'grey.500' }}>
                Deposits:{' '}
                <Box component="span" sx={{ color: '#4caf50', fontWeight: 600 }}>
                  {formatUsd(cashflowSummary.deposits)}
                </Box>
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'grey.500' }}>
                Withdrawals:{' '}
                <Box component="span" sx={{ color: '#f44336', fontWeight: 600 }}>
                  {formatUsd(cashflowSummary.withdrawals)}
                </Box>
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: 'grey.500' }}>
                Net Flow:{' '}
                <Box
                  component="span"
                  sx={{ color: pnlColor(cashflowSummary.net), fontWeight: 600 }}
                >
                  {formatUsd(cashflowSummary.net)}
                </Box>
              </Typography>
            </Stack>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cashflows?.map(cf => (
                <TableRow key={cf._id}>
                  <TableCell>
                    {new Date(cf.date).toISOString().split('T')[0]}
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    {cf.platform}
                  </TableCell>
                  <TableCell
                    sx={{
                      textTransform: 'capitalize',
                      color: cf.type === 'deposit' ? '#4caf50' : '#f44336',
                    }}
                  >
                    {cf.type}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: cf.type === 'deposit' ? '#4caf50' : '#f44336',
                    }}
                  >
                    {cf.type === 'withdrawal' ? '-' : '+'}
                    {formatUsd(cf.amountUsd)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
                    {cf.note || '-'}
                  </TableCell>
                </TableRow>
              ))}
              {cashflows && cashflows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography
                      sx={{ fontSize: '0.75rem', color: 'grey.600' }}
                    >
                      No deposits or withdrawals yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>

        <Dialog
          open={!!confirmDelete}
          onClose={() => setConfirmDelete(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: '0.95rem' }}>Delete cost?</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '0.82rem' }}>
              {confirmDelete?.description} — {formatUsd(confirmDelete?.amountUsd)}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={() => {
                if (confirmDelete) {
                  deleteCost(confirmDelete._id);
                  setConfirmDelete(null);
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <RecordDistributionDialog
          open={distributionOpen}
          onClose={() => setDistributionOpen(false)}
          currentMonthPnl={dividends?.currentPeriod.periodRealPnlUsd}
        />

        <Dialog
          open={!!confirmDeleteDistribution}
          onClose={() => setConfirmDeleteDistribution(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: '0.95rem' }}>
            Delete distribution?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '0.82rem' }}>
              Distribution of{' '}
              {formatUsd(confirmDeleteDistribution?.totalUsd)} on{' '}
              {fmtDate(confirmDeleteDistribution?.settledAt)} across{' '}
              {confirmDeleteDistribution?.lines.length ?? 0} partner line(s).
              {confirmDeleteDistribution?.legacyPaymentSource === 'platform' &&
                ' The linked withdrawal will also be removed.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              onClick={() => setConfirmDeleteDistribution(null)}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              variant="contained"
              onClick={() => {
                if (confirmDeleteDistribution) {
                  deleteDistribution(confirmDeleteDistribution._id);
                  setConfirmDeleteDistribution(null);
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
          </>
        )}
      </Box>
    </PageWithTitle>
  );
};
